import { truncate } from "./polyfills.js";

const MAX_MINUTES = 60;
const MAX_MINUTE_FRACTION = (MAX_MINUTES - 1)/MAX_MINUTES;
const COORDINATE_DECIMAL_PLACES = 12;
const DDM_MINUTE_PRECISION = 2;

/** Numeric input accepted from persisted coordinates and editable controls. */
export type CoordinateNumericInput = number | string | null | undefined;

/** Raw and display-rounded decimal-minute values. */
export interface DdmMinutes {
	raw?: CoordinateNumericInput;
	rounded?: CoordinateNumericInput;
}

/** Degrees-and-decimal-minutes input accepted by {@link ddmToDegrees}. */
export interface DdmValue {
	degrees?: CoordinateNumericInput;
	minutes?: DdmMinutes;
	isNegative?: boolean;
}

/** Lossless degrees-and-decimal-minutes representation of decimal degrees. */
export interface DdmConversion {
	degrees: number | undefined;
	minutes: {
		raw: number | undefined;
		rounded: number | undefined;
	};
	isNegative: boolean;
}

/** Editable two-decimal degrees-and-decimal-minutes decomposition. */
export interface DecomposedDdm {
	degrees: number | undefined;
	minutes: number | undefined;
	isNegative: boolean;
}

/** Degrees-and-minutes value used by the legacy degree-minute field. */
export interface DegreeMinuteValue {
	degrees: number | null;
	minutes: number | null;
	isNegative?: boolean;
}

/** Decomposed value returned to the legacy degree-minute field. */
export interface DegreeMinuteConversion {
	degrees: number;
	minutes: number;
	roundedMinutes: number;
	isNegative: boolean;
}

/**
 * Removes floating-point representation noise from a coordinate component.
 *
 * @param value - Coordinate component to normalize.
 * @returns The component rounded to the service's lossless comparison precision.
 */
function normalizeCoordinatePart(value: number): number {
	return Number(value.toFixed(COORDINATE_DECIMAL_PLACES));
}

/**
 * Determines whether a coordinate component can be converted to a finite number.
 *
 * @param value - Coordinate component to validate.
 * @returns Whether the component is present and finite.
 */
function isValidCoordinatePart(value: CoordinateNumericInput): boolean {
	return value !== undefined && value !== null && value !== "" && Number.isFinite(Number(value));
}

/**
 * Converts a degrees-and-decimal-minutes value to decimal degrees.
 *
 * @param ddmValue - DDM value whose minute component is supplied in `minutes.raw`.
 * @returns Decimal degrees, or `undefined` when required components or minute bounds are invalid.
 */
export const ddmToDegrees = (ddmValue: DdmValue | null | undefined): number | undefined => {
	if(!ddmValue || !isValidCoordinatePart(ddmValue.degrees) || !isValidCoordinatePart(ddmValue.minutes?.raw)) {
		return undefined;
	}

	let degrees = Math.abs(Number(ddmValue.degrees));
	let minutes = Number(ddmValue.minutes?.raw);

	if(minutes < 0 || minutes > MAX_MINUTES) {
		return undefined;
	}

	if(minutes === MAX_MINUTES) {
		degrees += 1;
		minutes = 0;
	}

	const result = degrees + (minutes / MAX_MINUTES);
	const signedResult = ddmValue.isNegative || Number(ddmValue.degrees) < 0 ? -result : result;
	return normalizeCoordinatePart(signedResult);
};

/**
 * Converts decimal degrees to a lossless degrees-and-decimal-minutes representation.
 *
 * Both `minutes.raw` and `minutes.rounded` retain the normalized source precision. Consumers
 * needing the two-decimal editable representation should use {@link getDecomposedDdm}.
 *
 * @param rawDegrees - Decimal degrees to decompose.
 * @returns DDM components and their sign, or undefined components for invalid input.
 */
export const degreesToDdm = (rawDegrees: CoordinateNumericInput): DdmConversion => {
	if(!Number.isFinite(Number(rawDegrees))) {
		return {
			degrees: undefined,
			minutes: {
				raw: undefined,
				rounded: undefined
			},
			isNegative: false
		};
	}

	const numericDegrees = Number(rawDegrees);
	let degrees = Math.floor(Math.abs(numericDegrees));
	let minutes = normalizeCoordinatePart((Math.abs(numericDegrees) - degrees) * MAX_MINUTES);

	if(minutes === MAX_MINUTES) {
		degrees += 1;
		minutes = 0;
	}

	return {
		degrees,
		minutes: {
			raw: minutes,
			rounded: minutes
		},
		isNegative: numericDegrees < 0
	};
};

/**
 * Decomposes decimal degrees into the two-decimal DDM shape used by editable coordinate controls.
 *
 * Rounding that reaches 60 minutes is carried into the degree component.
 *
 * @param rawDegrees - Decimal degrees to decompose for editing.
 * @returns Editable degree, minute, and sign components.
 */
export const getDecomposedDdm = (rawDegrees: CoordinateNumericInput): DecomposedDdm => {
	const value = degreesToDdm(rawDegrees);
	const rawMinutes = value.minutes.raw;
	if(value.degrees === undefined || rawMinutes === undefined || !Number.isFinite(rawMinutes)) {
		return {
			degrees: value.degrees,
			minutes: undefined,
			isNegative: value.isNegative
		};
	}

	const minutes = Number(rawMinutes.toFixed(DDM_MINUTE_PRECISION));
	if(minutes === MAX_MINUTES) {
		return {
			degrees: value.degrees + 1,
			minutes: 0,
			isNegative: value.isNegative
		};
	}

	return {
		degrees: value.degrees,
		minutes,
		isNegative: value.isNegative
	};
};

/**
 * Converts the legacy degrees-and-minutes value to decimal degrees.
 *
 * @param dmsValue - Legacy degree-minute field value.
 * @returns Decimal degrees rounded to three places, or `undefined` for invalid input.
 */
export const dmsToDegrees = (dmsValue: DegreeMinuteValue | null | undefined): number | undefined => {
	if(dmsValue && dmsValue.degrees !== null && !isNaN(dmsValue.degrees)
		&& dmsValue.minutes !== null && !isNaN(dmsValue.minutes) !== null) {

		let fractionalMinutes = (dmsValue.minutes / MAX_MINUTES);

		if(fractionalMinutes > MAX_MINUTE_FRACTION) {
			fractionalMinutes = 1;
		}

		let fullDegrees = dmsValue.degrees + fractionalMinutes;
		if(dmsValue.isNegative) {
			fullDegrees *= -1;
		}
		fullDegrees = parseFloat(fullDegrees.toFixed(3));
		return fullDegrees;
	}

	return undefined;
};

/**
 * Decomposes decimal degrees for the legacy degree-minute field.
 *
 * @param rawDegrees - Decimal degrees to decompose.
 * @param isStrict - Whether to preserve unrounded minute precision before carrying boundaries.
 * @returns Legacy degree-minute components and sign.
 */
export const degreesToDms = (rawDegrees: number, isStrict = false): DegreeMinuteConversion => {
	let { degrees, minutes } = isStrict ? strictDecompose(rawDegrees) : roundingDecompose(rawDegrees);

	let isNegative = false;
	if(rawDegrees < 0) {
		if(!isStrict) {
			degrees *= -1;
			minutes *= -1;
		}
		isNegative = true;
	}

	const dmsVal = {
		degrees,
		minutes,
		roundedMinutes: minutes,
		isNegative: isNegative
	};

	return dmsVal;

	// This function composes decimal degrees into DMS with rounding minutes to the nearest minute
	// This version is used when dealing with decimal degrees that have lost precision at some point.
	function roundingDecompose(fullDegrees: number): Pick<DegreeMinuteConversion, "degrees" | "minutes"> {
		const degrees = truncate(fullDegrees);
		const minutesAsDecimal = fullDegrees - degrees;

		const minutes = Math.round(minutesAsDecimal * MAX_MINUTES);

		return { degrees, minutes };
	}

	// from https://stackoverflow.com/questions/5786025/decimal-degrees-to-degrees-minutes-and-seconds-in-javascript/64866682#64866682
	// This function composes decimal degrees into DMS with rolling over 60 seconds into minutes and 60 minutes into degrees.
	// This version is used when the decimal degree has not lost any precision in conversion yet.
	function strictDecompose(fullDegrees: number): Pick<DegreeMinuteConversion, "degrees" | "minutes"> {
		let d = parseInt(fullDegrees.toString());
		const minfloat = Math.abs((fullDegrees - d) * 60);
		let m = Math.floor(minfloat);
		const secfloat = (minfloat - m) * 60;
		let s = Math.round((secfloat + Number.EPSILON) * 100) / 100;
		d = Math.abs(d);

		if(s == 60) {
			m++;
			s = 0;
		}
		if(m == 60) {
			d++;
			m = 0;
		}

		const dms = {
			degrees: d,
			minutes: m
		};
		return dms;
	}
};
